import angular from 'angular';

import csmUIEvents from '../events/events';
import csmUINumberInput from '../numberInput/numberInput';
import './numericStepper.css';

export default angular
  .module('csm.ui.numericStepper', [csmUIEvents, csmUINumberInput])
  /**
   * @ngdoc    component
   * @module   csm.ui
   * @name     csm.ui.numericStepper.component:numericStepper
   *
   * @param  {expression} blankIsNan  When true, a blank input will set the model to
   *                                  a NaN value. Otherwise 0.
   * @param  {expression} enableLoop  When the min and max have been set and the value
   *                                  will exceed either depending on the current direction,
   *                                  if enabled the value will be set to the max when
   *                                  decrementing or the min when incrementing.
   * @param  {expression} max         Maximum model value when set
   * @param  {expression} min         Minimum model value when set
   * @param  {expression} onLoop      Expression to evaluate when a loop has occurred. The
   *                                  expression will have **$isRollover** available which
   *                                  when true indicates a loop to the min value, otherwise
   *                                  indicates a loop to the max value.
   * @param  {expression} precision   Precision to utilize for value management
   * @param  {expression} stepSize    Increment/decrement amount. Default=1
   *
   * @description
   * Provides a mean to increment/decrement a numeric value by a certain amount
   * (step size) using buttons or the up/down arrow keys.
   *******************************************************************************/
  .component('numericStepper', {
    bindings: {
      blankIsNan: '&',
      enableLoop: '&',
      onLoop: '&',
      max: '&',
      min: '&',
      precision: '&',
      stepSize: '&'
    },
    require: { ngModelCtrl: 'ngModel' },
    template:
      '<input class="form-control" type="text" number-input ng-model="$ctrl.val" blank-is-nan="$ctrl.blankIsNan()" precision="$ctrl.getPrecision()" ng-disabled="$ctrl.isDisabled"/>' +
      '<div class="steppers">' +
      '<span class="btn btn-default glyphicon glyphicon-chevron-up"   csm-mousepress="$ctrl.upPress()"   ng-disabled="$ctrl.isDisabled"></span>' +
      '<span class="btn btn-default glyphicon glyphicon-chevron-down" csm-mousepress="$ctrl.downPress()" ng-disabled="$ctrl.isDisabled"></span>' +
      '</div>',
    controller: function ($scope, $element, $timeout, KEYCODES, Tools) {
      /** Self Reference. */ var mThis = this;
      /** JQLite Number input reference. */ var mInput = $element.find('input');

      /** Enable looping when flag is true and both min and max are set. */
      function allowLoop() {
        return !!mThis.enableLoop() && !isNaN(getMax()) && !isNaN(getMin());
      }

      /** Perform decrement of value. */
      function decrease() {
        var newVal = getNextVal();
        var min = getMin();

        /*-----------------------------------*/
        /* If bound exceeded, try to loop if */
        /* allowed, otherwise set to the min */
        /*-----------------------------------*/
        if (!isNaN(min) && newVal < min) {
          if (allowLoop()) {
            doLoop();
            return;
          } else newVal = min;
        }

        mThis.val = newVal;
      }

      /** Update val based on the isRollover flag and execute binding to notify parent component. */
      function doLoop(isRollover) {
        mThis.val = isRollover ? getMin() : getMax();
        mThis.onLoop({ $isRollover: !!isRollover });
      }

      /** Mousepress down button handler. */
      mThis.downPress = function () {
        decrease();
        $timeout(function () {
          mInput[0].focus();
        });
      };

      /** Parse the max expression for a numeric value */
      function getMax() {
        var max = mThis.max();
        var maxNum = Number(max);
        var isMaxNumeric = !Tools.isEmpty(max) && !isNaN(maxNum);

        return isMaxNumeric ? maxNum : NaN;
      }

      /** Parse the min expression for a numeric value */
      function getMin() {
        var min = mThis.min();
        var minNum = Number(min);
        var isMinNumeric = !Tools.isEmpty(min) && !isNaN(minNum);

        return isMinNumeric ? minNum : NaN;
      }

      /** Retrieve the next value to be based on the doIncrease flag. */
      function getNextVal(doIncrease) {
        var current = mThis.val || 0;
        return doIncrease ? current + getStepSize() : current - getStepSize();
      }

      /** Parse the precision expression. If NaN, use the stepSize to determine 
        the precision. */
      mThis.getPrecision = function () {
        var p = Number(mThis.precision());

        if (!isNaN(p)) return p;

        /*------------------------------------*/
        /* Convert to string to calculate the */
        /* number of digits after the decimal */
        /*------------------------------------*/
        var ss = getStepSize().toString();
        var afterDecimal = ss.split('.')[1];
        return (afterDecimal || '').length;
      };

      /** Parse the step size to use. Default=1. */
      function getStepSize() {
        return Number(mThis.stepSize()) || 1;
      }

      /** Perform increment of value. */
      function increase() {
        var newVal = getNextVal(true);
        var max = getMax();

        /*-----------------------------------*/
        /* If bound exceeded, try to loop if */
        /* allowed, otherwise set to the max */
        /*-----------------------------------*/
        if (!isNaN(max) && newVal > max) {
          if (allowLoop()) {
            doLoop(true);
            return;
          } else newVal = max;
        }

        mThis.val = newVal;
      }

      /** Normalize the current value. A timeout is used
        to ensure the value has been updated by the numberInput. */
      function normalizeVal() {
        $timeout(function () {
          setValue(mThis.val);
        });
      }

      /** Ensure the value to set falls within the bounds if provided. */
      function setValue(newVal) {
        if (isNaN(newVal) || typeof newVal != 'number') newVal = NaN;

        var max = getMax();
        if (!isNaN(max) && newVal > max) newVal = max;

        var min = getMin();
        if (!isNaN(min) && newVal < min) newVal = min;

        mThis.val = newVal;
      }

      /** Mousepress up button handler. */
      mThis.upPress = function () {
        increase();
        $timeout(function () {
          mInput[0].focus();
        });
      };

      /*----------------------------------------*/
      /* Set up ngModelController functionality */
      /*----------------------------------------*/
      mThis.$onInit = function () {
        /*-------------------------------------------------------------*/
        /* Render is called when model is changed outside of component */
        /*-------------------------------------------------------------*/
        mThis.ngModelCtrl.$render = function () {
          setValue(mThis.ngModelCtrl.$viewValue);

          /*----------------------------*/
          /* Ensure the view is updated */
          /*----------------------------*/
          if (mThis.val != mThis.ngModelCtrl.$viewValue)
            mThis.ngModelCtrl.$setViewValue(mThis.val);
        };

        /*------------------------------------------------*/
        /* Update the model when the val property changes */
        /*------------------------------------------------*/
        $scope.$watch(
          function () {
            return mThis.val;
          },
          function (newVal) {
            mThis.ngModelCtrl.$setViewValue(newVal);
          }
        );
      };

      /*------------------------------------------------*/
      /* Set up watch to monitor the disabled attribute */
      /*------------------------------------------------*/
      $scope.$watch(
        function () {
          return $element[0].hasAttribute('disabled');
        },
        function (disabled) {
          mThis.isDisabled = disabled;
        }
      );

      /*--------------------------------------------------*/
      /* Ensure cursor is at the end of the text on focus */
      /*--------------------------------------------------*/
      mInput.on('focus', function () {
        var lastIndex = mInput.val().length;
        mInput[0].setSelectionRange(lastIndex, lastIndex);
      });

      /*------------------------------------------*/
      /* Set up keydown listeners for the up/down */
      /* arrow keys to increment/decrement value  */
      /*------------------------------------------*/
      mInput.on('keydown', function (event) {
        var key = event.which || event.keyCode;

        if (key == KEYCODES.UP_ARROW) $scope.$apply(increase);
        else if (key == KEYCODES.DOWN_ARROW) $scope.$apply(decrease);
      });

      /*------------------------------------------*/
      /* Normalize the value on input interaction */
      /*------------------------------------------*/
      mInput.on('blur', normalizeVal);
      mInput.on('paste', normalizeVal);
    }
  })
  .name;
